import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, MapPin, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";
import { WARDS, PRIORITIES, ISSUE_CATEGORIES, VOTING_OPTIONS, COUNCIL_SATISFACTION, DIRECTION_OPTIONS, RESIDENT_YEARS } from "@/lib/constants";

const STEPS = ["Your ward", "Your priorities", "Report an issue", "About you", "Review"];

export default function Survey() {
  const navigate = useNavigate();
  const location = useLocation();
  const preWard = location.state?.ward && WARDS.includes(location.state.ward) ? location.state.ward : "";

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [ward, setWard] = useState(preWard);
  const [priorities, setPriorities] = useState([]);
  const [topPriority, setTopPriority] = useState("");
  const [priorityComment, setPriorityComment] = useState("");
  const [councilSatisfaction, setCouncilSatisfaction] = useState("");
  const [rightDirection, setRightDirection] = useState("");
  const [voting, setVoting] = useState("");

  const [hasIssue, setHasIssue] = useState(false);
  const [issueCategory, setIssueCategory] = useState("");
  const [issueLocation, setIssueLocation] = useState("");
  const [issueSeverity, setIssueSeverity] = useState("medium");
  const [issueDescription, setIssueDescription] = useState("");

  const [anonymous, setAnonymous] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [postcode, setPostcode] = useState("");
  const [phone, setPhone] = useState("");
  const [optIn, setOptIn] = useState(false);
  const [yearsResident, setYearsResident] = useState("");

  const togglePriority = (label) => {
    setPriorities((prev) => {
      if (prev.includes(label)) {
        if (topPriority === label) setTopPriority("");
        return prev.filter((p) => p !== label);
      }
      if (prev.length >= 5) {
        toast.info("You can pick up to 5 priorities.");
        return prev;
      }
      return [...prev, label];
    });
  };

  const canNext = () => {
    if (step === 0) return !!ward;
    if (step === 1) return priorities.length > 0 && !!topPriority;
    if (step === 2) return !hasIssue || (!!issueCategory && !!issueDescription);
    return true;
  };

  const next = () => {
    if (!canNext()) {
      toast.error(step === 0 ? "Please select your ward." : step === 1 ? "Pick at least one priority and choose your top one." : "Please complete the issue details.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/survey", {
        ward,
        priorities,
        top_priority: topPriority,
        priority_comment: priorityComment || null,
        council_satisfaction: councilSatisfaction || null,
        right_direction: rightDirection || null,
        years_resident: anonymous ? (yearsResident || null) : (yearsResident || null),
        voting_intention: voting || null,
        issue: {
          has_issue: hasIssue,
          category: hasIssue ? issueCategory : null,
          location: hasIssue ? issueLocation : null,
          severity: hasIssue ? issueSeverity : null,
          description: hasIssue ? issueDescription : null,
        },
        contact: {
          name: anonymous ? null : name || null,
          email: anonymous ? null : email || null,
          postcode: anonymous ? null : postcode || null,
          phone: anonymous ? null : phone || null,
          opt_in: anonymous ? false : optIn,
        },
        anonymous,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0A192F] text-white flex flex-col">
        <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 h-16 flex items-center">
          <Logo light />
        </div>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-lg text-center" data-testid="survey-success">
            <div className="mx-auto h-20 w-20 rounded-full bg-[#12B6CF]/15 border border-[#12B6CF]/40 flex items-center justify-center">
              <CheckCircle2 className="text-[#12B6CF]" size={44} />
            </div>
            <h1 className="mt-8 font-display font-extrabold text-4xl tracking-tight">Thank you.</h1>
            <p className="mt-4 text-slate-300 text-lg leading-relaxed">
              Your response has been recorded for <span className="text-[#12B6CF] font-semibold">{ward}</span>. Every voice helps us fight for the things that matter across Solihull.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="inline-flex items-center justify-center rounded-full bg-[#12B6CF] hover:bg-[#0FA0B7] text-white font-semibold px-7 py-3.5 transition-colors" data-testid="success-home">
                Back to home
              </Link>
              <button onClick={() => window.location.reload()} className="inline-flex items-center justify-center rounded-full border border-white/20 hover:border-white/40 text-white font-semibold px-7 py-3.5 transition-colors" data-testid="success-another">
                Submit another response
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const pct = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo />
          <Link to="/" className="text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors" data-testid="survey-exit">Exit</Link>
        </div>
        <div className="h-1.5 bg-slate-100">
          <div className="h-full bg-[#12B6CF] transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-10">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A788A]" data-testid="survey-step-label">
          Step {step + 1} of {STEPS.length} · {STEPS[step]}
        </p>

        {/* STEP 0 - WARD */}
        {step === 0 && (
          <div className="mt-4">
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0F172A] tracking-tight">Which ward do you live in?</h1>
            <p className="mt-3 text-slate-600 leading-relaxed">This helps us group your feedback with your neighbours across Solihull.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-2.5" data-testid="ward-grid">
              {WARDS.map((w) => (
                <button
                  key={w}
                  onClick={() => setWard(w)}
                  data-testid={`ward-option-${w}`}
                  className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 text-left transition-all ${
                    ward === w ? "border-[#12B6CF] bg-[#E6F8FA] ring-2 ring-[#12B6CF]/30" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <MapPin size={18} className={ward === w ? "text-[#0A788A]" : "text-slate-400"} />
                  <span className="text-sm font-semibold text-slate-800">{w}</span>
                  {ward === w && <Check size={16} className="ml-auto text-[#0A788A]" />}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 1 - PRIORITIES */}
        {step === 1 && (
          <div className="mt-4">
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0F172A] tracking-tight">What matters most to you?</h1>
            <p className="mt-3 text-slate-600 leading-relaxed">Select up to 5 issues, then tap the star on your single most important priority.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-2.5" data-testid="priority-grid">
              {PRIORITIES.map((p) => {
                const selected = priorities.includes(p.label);
                const isTop = topPriority === p.label;
                return (
                  <div
                    key={p.id}
                    className={`rounded-xl border p-4 transition-all ${selected ? "border-[#12B6CF] bg-[#E6F8FA]" : "border-slate-200 bg-white hover:border-slate-300"}`}
                  >
                    <button onClick={() => togglePriority(p.label)} data-testid={`priority-option-${p.id}`} className="w-full text-left flex items-start gap-3">
                      <span className={`mt-0.5 h-5 w-5 rounded-md border flex items-center justify-center shrink-0 ${selected ? "bg-[#12B6CF] border-[#12B6CF]" : "border-slate-300"}`}>
                        {selected && <Check size={13} className="text-white" />}
                      </span>
                      <span>
                        <span className="block text-sm font-bold text-slate-800">{p.label}</span>
                        <span className="block text-xs text-slate-500 mt-0.5">{p.desc}</span>
                      </span>
                    </button>
                    {selected && (
                      <button
                        onClick={() => setTopPriority(p.label)}
                        data-testid={`priority-top-${p.id}`}
                        className={`mt-3 w-full rounded-lg py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                          isTop ? "bg-[#0A192F] text-[#12B6CF]" : "bg-white border border-slate-200 text-slate-500 hover:border-[#12B6CF]"
                        }`}
                      >
                        {isTop ? "★ Your top priority" : "☆ Set as top priority"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Council satisfaction */}
            <div className="mt-8">
              <label className="text-sm font-semibold text-slate-700">How satisfied are you with the way Solihull Council is currently run?</label>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {COUNCIL_SATISFACTION.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setCouncilSatisfaction(councilSatisfaction === opt ? "" : opt)}
                    data-testid={`council-sat-${opt}`}
                    className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all ${councilSatisfaction === opt ? "border-[#12B6CF] bg-[#E6F8FA] text-[#0A788A]" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            {/* Direction */}
            <div className="mt-6">
              <label className="text-sm font-semibold text-slate-700">Overall, do you feel Solihull is heading in the right direction?</label>
              <div className="mt-2.5 grid grid-cols-3 gap-2.5">
                {DIRECTION_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setRightDirection(rightDirection === opt ? "" : opt)}
                    data-testid={`direction-${opt}`}
                    className={`rounded-lg border py-2.5 px-2 text-sm font-semibold transition-all ${rightDirection === opt ? "border-[#12B6CF] bg-[#E6F8FA] text-[#0A788A]" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <label className="text-sm font-semibold text-slate-700">Anything else on your mind? (optional)</label>
              <textarea
                value={priorityComment}
                onChange={(e) => setPriorityComment(e.target.value)}
                data-testid="priority-comment"
                rows={3}
                placeholder="Tell us in your own words what needs to change in Solihull."
                className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-[#12B6CF] focus:ring-2 focus:ring-[#12B6CF]/20 outline-none"
              />
            </div>
          </div>
        )}

        {/* STEP 2 - ISSUE */}
        {step === 2 && (
          <div className="mt-4">
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0F172A] tracking-tight">Is there a local problem to report?</h1>
            <p className="mt-3 text-slate-600 leading-relaxed">A pothole, fly-tipping or a broken streetlight. Tell us and we&apos;ll take it up locally. This is optional.</p>
            <div className="mt-8 grid grid-cols-2 gap-2.5">
              <button onClick={() => setHasIssue(false)} data-testid="issue-no" className={`rounded-xl border px-4 py-4 font-semibold text-sm transition-all ${!hasIssue ? "border-[#12B6CF] bg-[#E6F8FA] text-[#0A788A]" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
                No, skip this
              </button>
              <button onClick={() => setHasIssue(true)} data-testid="issue-yes" className={`rounded-xl border px-4 py-4 font-semibold text-sm transition-all ${hasIssue ? "border-[#12B6CF] bg-[#E6F8FA] text-[#0A788A]" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
                Yes, report an issue
              </button>
            </div>
            {hasIssue && (
              <div className="mt-6 space-y-5 rounded-2xl border border-slate-200 bg-white p-5">
                <div>
                  <label className="text-sm font-semibold text-slate-700">Type of issue</label>
                  <select value={issueCategory} onChange={(e) => setIssueCategory(e.target.value)} data-testid="issue-category" className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-[#12B6CF] focus:ring-2 focus:ring-[#12B6CF]/20 outline-none">
                    <option value="">Select a category</option>
                    {ISSUE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Where is it? (street, landmark or postcode)</label>
                  <input value={issueLocation} onChange={(e) => setIssueLocation(e.target.value)} data-testid="issue-location" placeholder="e.g. Warwick Road near the library" className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-[#12B6CF] focus:ring-2 focus:ring-[#12B6CF]/20 outline-none" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">How urgent is it?</label>
                  <div className="mt-2 grid grid-cols-3 gap-2.5">
                    {[["low", "Low"], ["medium", "Medium"], ["high", "High or Danger"]].map(([v, l]) => (
                      <button key={v} onClick={() => setIssueSeverity(v)} data-testid={`issue-severity-${v}`} className={`rounded-lg border py-2.5 text-sm font-semibold transition-all ${issueSeverity === v ? "border-[#12B6CF] bg-[#E6F8FA] text-[#0A788A]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Describe the problem</label>
                  <textarea value={issueDescription} onChange={(e) => setIssueDescription(e.target.value)} data-testid="issue-description" rows={3} placeholder="Tell us what is wrong." className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-[#12B6CF] focus:ring-2 focus:ring-[#12B6CF]/20 outline-none" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 3 - ABOUT YOU */}
        {step === 3 && (
          <div className="mt-4">
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0F172A] tracking-tight">A little about you</h1>
            <p className="mt-3 text-slate-600 leading-relaxed">All optional. You can submit completely anonymously and your feedback still counts.</p>

            <div className="mt-8">
              <label className="text-sm font-semibold text-slate-700">How long have you lived in Solihull?</label>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {RESIDENT_YEARS.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setYearsResident(yearsResident === opt ? "" : opt)}
                    data-testid={`years-${opt}`}
                    className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all ${yearsResident === opt ? "border-[#12B6CF] bg-[#E6F8FA] text-[#0A788A]" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <label className="text-sm font-semibold text-slate-700">If a local election were held tomorrow, who would you vote for? (optional)</label>
              <select value={voting} onChange={(e) => setVoting(e.target.value)} data-testid="voting-intention" className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-[#12B6CF] focus:ring-2 focus:ring-[#12B6CF]/20 outline-none">
                <option value="">Prefer not to say</option>
                {VOTING_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} data-testid="anonymous-toggle" className="mt-1 h-5 w-5 rounded accent-[#12B6CF]" />
                <span>
                  <span className="block text-sm font-bold text-slate-800">Submit anonymously</span>
                  <span className="block text-xs text-slate-500 mt-0.5">We won&apos;t collect any of your contact details.</span>
                </span>
              </label>
            </div>

            {!anonymous && (
              <div className="mt-5 space-y-5 rounded-2xl border border-slate-200 bg-white p-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-semibold text-slate-700">Name</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} data-testid="contact-name" className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#12B6CF] focus:ring-2 focus:ring-[#12B6CF]/20 outline-none" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-700">Postcode</label>
                    <input value={postcode} onChange={(e) => setPostcode(e.target.value)} data-testid="contact-postcode" placeholder="B91" className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#12B6CF] focus:ring-2 focus:ring-[#12B6CF]/20 outline-none" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-700">Email</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="contact-email" className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#12B6CF] focus:ring-2 focus:ring-[#12B6CF]/20 outline-none" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-700">Phone (optional)</label>
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="contact-phone" className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#12B6CF] focus:ring-2 focus:ring-[#12B6CF]/20 outline-none" />
                  </div>
                </div>
                <label className="flex items-start gap-3 cursor-pointer border-t border-slate-100 pt-4">
                  <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} data-testid="contact-optin" className="mt-1 h-5 w-5 rounded accent-[#12B6CF]" />
                  <span className="text-sm text-slate-700">Yes, keep me updated on Reform UK Solihull&apos;s work on these issues.</span>
                </label>
              </div>
            )}
          </div>
        )}

        {/* STEP 4 - REVIEW */}
        {step === 4 && (
          <div className="mt-4">
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0F172A] tracking-tight">Check and submit</h1>
            <p className="mt-3 text-slate-600 leading-relaxed">Please review your answers before submitting.</p>
            <div className="mt-8 space-y-3" data-testid="review-panel">
              <ReviewRow label="Ward" value={ward} />
              <ReviewRow label="Top priority" value={topPriority || "Not selected"} />
              <ReviewRow label="Priorities" value={priorities.join(", ") || "None selected"} />
              {councilSatisfaction && <ReviewRow label="Council rating" value={councilSatisfaction} />}
              {rightDirection && <ReviewRow label="Direction" value={rightDirection} />}
              {priorityComment && <ReviewRow label="Your comment" value={priorityComment} />}
              <ReviewRow label="Issue reported" value={hasIssue ? `${issueCategory} (${issueSeverity})` : "None"} />
              {hasIssue && issueDescription && <ReviewRow label="Issue details" value={`${issueLocation ? issueLocation + " · " : ""}${issueDescription}`} />}
              {yearsResident && <ReviewRow label="Years in Solihull" value={yearsResident} />}
              <ReviewRow label="Submission" value={anonymous ? "Anonymous" : `${name || "No name"} · ${email || "no email"}`} />
              {voting && <ReviewRow label="Voting intention" value={voting} />}
            </div>
            <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
              <ShieldCheck size={16} className="text-[#12B6CF]" /> Your response is stored securely and used to shape our local priorities.
            </div>
          </div>
        )}

        {/* NAV */}
        <div className="mt-10 flex items-center justify-between gap-3">
          {step > 0 ? (
            <button onClick={back} data-testid="survey-back" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 font-semibold text-slate-700 hover:bg-slate-100 transition-colors">
              <ArrowLeft size={18} /> Back
            </button>
          ) : <span />}
          {step < STEPS.length - 1 ? (
            <button onClick={next} data-testid="survey-next" className="inline-flex items-center gap-2 rounded-full bg-[#12B6CF] hover:bg-[#0FA0B7] text-white px-8 py-3 font-semibold transition-colors">
              Continue <ArrowRight size={18} />
            </button>
          ) : (
            <button onClick={submit} disabled={submitting} data-testid="survey-submit" className="inline-flex items-center gap-2 rounded-full bg-[#0A192F] hover:bg-[#12233f] text-white px-8 py-3 font-semibold transition-colors disabled:opacity-60">
              {submitting ? "Submitting" : "Submit my response"} <Check size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-400 sm:w-40 shrink-0">{label}</span>
      <span className="text-sm font-semibold text-slate-800">{value}</span>
    </div>
  );
}
